import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription, distinctUntilChanged, firstValueFrom, skip } from 'rxjs';
import { CompanyFilterService } from '@services/company-filter/company-filter.service';
import { FilemanagerApiService } from '@services/api/filemanager/filemanager-api.service';
import { FilemanagerService } from '@app/core/services/filemanager/filemanager.service';
import { RoomsService } from '@app/core/services/api/rooms/rooms.service';
import { ExplorerEntity } from '@interfaces/explorer-entity';

/** A filemanager row with the fields the list API returns besides ExplorerEntity. */
interface Item extends ExplorerEntity {
  created_at?: string;
  updated_at?: string;
  dimension_file?: number;
  is_call_conversation?: boolean;
}

interface ConversationDay {
  folder: Item;
  day: string;
  open: boolean;
  loading: boolean;
  files: Item[] | null;
}

interface PhotoDay {
  day: string;
  photos: Item[];
}

type DateFilter = 'all' | 'today' | 'week' | 'month' | 'range';

const CONVERSATIONS_ROOT = 'Conversazioni chiamate';
// Folders the panel fills by itself: photos taken from the video and images sent during a call
const PHOTO_FOLDERS = ['screenshot', 'tmp'];

/**
 * Home of "Documenti": the same files grouped by use (call conversations, manuals, photos) with readable
 * names and a date filter. Folders still open in the full explorer, where files are uploaded and organised.
 */
@Component({
  selector: 'app-documents-home',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, TranslateModule, TablerIconsModule],
  templateUrl: './documents-home.component.html',
  styleUrl: './documents-home.component.scss'
})
export class DocumentsHomeComponent implements OnInit, OnDestroy {
  private api = inject(FilemanagerApiService);
  private files = inject(FilemanagerService);
  private rooms = inject(RoomsService);
  private translate = inject(TranslateService);
  private companyFilter = inject(CompanyFilterService);
  private companySubscription: Subscription | null = null;

  @Output() openFolder = new EventEmitter<number>();
  @Output() browseAll = new EventEmitter<void>();

  loading = true;
  failed = false;
  filter: DateFilter = 'all';
  from = '';
  to = '';
  readonly filters: DateFilter[] = ['all', 'today', 'week', 'month', 'range'];

  private conversationDays: ConversationDay[] = [];
  private manuals: Item[] = [];
  private photos: Item[] = [];
  private roomNames = new Map<string, string>();

  ngOnInit(): void {
    // The company choice is known first (the superadmin starts from his own): load with it, then again at every change
    this.companyFilter.load().finally(() => {
      this.companySubscription = this.companyFilter.documentsCompany$.pipe(distinctUntilChanged(), skip(1))
        .subscribe(() => this.load());
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.companySubscription?.unsubscribe();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.failed = false;
    try {
      await this.companyFilter.load();
      this.loadRoomNames();
      const root = await this.list(null);
      const conversationsRoot = root.find(item => item.type === 'folder' && item.name === CONVERSATIONS_ROOT);
      const photoFolders = root.filter(item => item.type === 'folder' && PHOTO_FOLDERS.includes(item.name));

      this.manuals = root
        .filter(item => item !== conversationsRoot && !photoFolders.includes(item))
        .sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name, 'it') : a.type === 'folder' ? -1 : 1));

      this.conversationDays = conversationsRoot
        ? (await this.list(conversationsRoot.id))
          .filter(item => item.type === 'folder')
          .map(folder => ({ folder, day: folder.name, open: false, loading: false, files: null }))
          .sort((a, b) => b.day.localeCompare(a.day))
        : [];
      if (this.conversationDays.length) {
        this.toggleDay(this.conversationDays[0]);
      }

      // Photo folders and one level of subfolders (e.g. tmp/ko)
      const photoLists = await Promise.all(photoFolders.map(folder => this.list(folder.id)));
      const nested = photoLists.flat().filter(item => item.type === 'folder');
      const nestedLists = await Promise.all(nested.map(folder => this.list(folder.id)));
      this.photos = [...photoLists.flat(), ...nestedLists.flat()]
        .filter(item => item.type === 'file')
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    } catch {
      this.failed = true;
    }
    this.loading = false;
  }

  private async list(parent: number | null): Promise<Item[]> {
    // The superadmin sees one company at a time: the top level of the company chosen in the menu
    const company = parent === null && this.companyFilter.isSuperadmin ? this.companyFilter.documentsCompany : null;
    const response: any = await firstValueFrom(this.api.getList(parent, '-created_at', false, company));
    return (response?.data ?? []) as Item[];
  }

  private loadRoomNames(): void {
    this.rooms.getRooms(false).subscribe({
      next: response => (response?.data ?? []).forEach(room => room.uuid && this.roomNames.set(room.uuid, room.name)),
      error: () => undefined
    });
  }

  async toggleDay(day: ConversationDay): Promise<void> {
    day.open = !day.open;
    if (day.open && day.files === null) {
      day.loading = true;
      try {
        day.files = (await this.list(day.folder.id))
          .filter(item => item.type === 'file')
          .sort((a, b) => b.name.localeCompare(a.name));
      } catch {
        day.files = [];
      }
      day.loading = false;
    }
  }

  view(item: Item): void {
    this.files.viewFile(item);
  }

  download(item: Item, event: Event): void {
    event.stopPropagation();
    this.files.downloadFile(item).subscribe();
  }

  setFilter(filter: DateFilter): void {
    this.filter = filter;
  }

  // ---- date filter -------------------------------------------------------------------------------

  private get range(): [string, string] | null {
    const today = this.isoDay(new Date());
    const daysAgo = (n: number) => this.isoDay(new Date(Date.now() - n * 86400000));
    switch (this.filter) {
      case 'today':
        return [today, today];
      case 'week':
        return [daysAgo(6), today];
      case 'month':
        return [daysAgo(29), today];
      case 'range':
        return this.from || this.to ? [this.from || '0000-01-01', this.to || '9999-12-31'] : null;
      default:
        return null;
    }
  }

  private inRange(day: string | undefined): boolean {
    const range = this.range;
    if (!range) {
      return true;
    }
    return !!day && day >= range[0] && day <= range[1];
  }

  private dayOf(item: Item): string {
    return item.created_at ? this.isoDay(new Date(item.created_at)) : '';
  }

  get visibleConversationDays(): ConversationDay[] {
    return this.conversationDays.filter(day => this.inRange(day.day));
  }

  get visibleManuals(): Item[] {
    if (!this.range) {
      return this.manuals;
    }
    return this.manuals.filter(item => this.inRange(this.isoDay(new Date(item.updated_at || item.created_at || 0))));
  }

  get visiblePhotoDays(): PhotoDay[] {
    const groups = new Map<string, Item[]>();
    for (const photo of this.photos) {
      const day = this.dayOf(photo);
      if (this.inRange(day)) {
        groups.set(day, [...(groups.get(day) ?? []), photo]);
      }
    }
    return [...groups.entries()].map(([day, photos]) => ({ day, photos }));
  }

  get photoCount(): number {
    return this.photos.length;
  }

  get filteredOut(): boolean {
    return !!this.range && !this.visibleConversationDays.length && !this.visibleManuals.length && !this.visiblePhotoDays.length;
  }

  // ---- readable names (display only, nothing is renamed) -----------------------------------------

  /** "14-38 - Stanza test visore - Test Visore.pdf" → "14:38 · Stanza test visore · Test Visore" */
  conversationTitle(item: Item): string {
    const match = item.name.replace(/\.pdf$/i, '').match(/^(\d{2})-(\d{2}) - (.+)$/);
    return match ? `${match[1]}:${match[2]} · ${match[3].replace(/ - /g, ' · ')}` : item.name;
  }

  photoTitle(item: Item): string {
    const name = item.name;
    const screenshot = name.match(/^screenshot_(\d{13})_([0-9a-f-]{36})/i);
    if (screenshot) {
      const room = this.roomNames.get(screenshot[2]);
      const label = `${this.translate.instant('filemanager.home.photo')} ${this.time(new Date(Number(screenshot[1])))}`;
      return room ? `${label} · ${room}` : label;
    }
    const tmp = name.match(/^tmp_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/);
    if (tmp) {
      return `${this.translate.instant('filemanager.home.image')} ${tmp[4]}:${tmp[5]}`;
    }
    const page = name.match(/^(.+?)_page_(\d+)/);
    if (page) {
      return `${page[1].replace(/_/g, ' ')} — ${this.translate.instant('filemanager.home.page')} ${page[2]}`;
    }
    return name.replace(/(\.(jpe?g|png))+$/i, '');
  }

  // ---- formatting --------------------------------------------------------------------------------

  private get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    return { it: 'it-IT', en: 'en-GB', es: 'es-ES', de: 'de-DE' }[lang] ?? 'it-IT';
  }

  private isoDay(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(date);
  }

  private time(date: Date): string {
    return new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }).format(date);
  }

  dayLabel(day: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      return day;
    }
    const [year, month, date] = day.split('-').map(Number);
    const label = new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      .format(new Date(year, month - 1, date, 12));
    const today = this.isoDay(new Date());
    const yesterday = this.isoDay(new Date(Date.now() - 86400000));
    const prefix = day === today ? this.translate.instant('conversations.today') + ', '
      : day === yesterday ? this.translate.instant('conversations.yesterday') + ', ' : '';
    return prefix + label;
  }

  shortDate(value?: string): string {
    if (!value) {
      return '';
    }
    return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(value));
  }

  size(bytes?: number): string {
    if (!bytes) {
      return '';
    }
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  }

  kind(item: Item): 'pdf' | 'img' | 'file' {
    const type = item.content_type || item['content-type'] || '';
    if (type === 'application/pdf' || /\.pdf$/i.test(item.name)) {
      return 'pdf';
    }
    return type.startsWith('image/') ? 'img' : 'file';
  }

  aiKey(item: Item): string | null {
    switch (item.rag_status) {
      case 'analyzed':
        return 'filemanager.ui.rag_ready';
      case 'pending':
        return 'filemanager.ui.rag_pending';
      case 'error':
        return 'filemanager.ui.rag_error';
      default:
        return this.kind(item) === 'pdf' ? 'filemanager.ui.rag_not_read' : null;
    }
  }

  aiClass(item: Item): string {
    return { analyzed: 'xr-badge--ok', pending: 'xr-badge--wait', error: 'xr-badge--busy' }[item.rag_status as string] ?? 'xr-badge--off';
  }
}
